export type SequentialUploadBatchSuccess<TItem, TResult> = {
  item: TItem;
  index: number;
  result: TResult;
  attempts: number;
};

export type SequentialUploadBatchFailure<TItem> = {
  item: TItem;
  index: number;
  error: Error;
  attempts: number;
};

export type SequentialUploadBatchResult<TItem, TResult> = {
  successes: Array<SequentialUploadBatchSuccess<TItem, TResult>>;
  failures: Array<SequentialUploadBatchFailure<TItem>>;
  stopped: boolean;
};

export type SequentialUploadBatchOptions<TItem> = {
  maxAttempts?: number;
  retryDelayMs?: number;
  shouldStop?: () => boolean;
  onAttempt?: (item: TItem, index: number, attempt: number) => void;
  onRetry?: (
    item: TItem,
    index: number,
    attempt: number,
    error: Error
  ) => void;
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const wait = (delayMs: number): Promise<void> =>
  delayMs > 0
    ? new Promise((resolve) => setTimeout(resolve, delayMs))
    : Promise.resolve();

export async function runSequentialUploadBatch<TItem, TResult>(
  items: TItem[],
  uploadOne: (
    item: TItem,
    index: number,
    attempt: number
  ) => Promise<TResult>,
  options: SequentialUploadBatchOptions<TItem> = {}
): Promise<SequentialUploadBatchResult<TItem, TResult>> {
  const successes: Array<SequentialUploadBatchSuccess<TItem, TResult>> = [];
  const failures: Array<SequentialUploadBatchFailure<TItem>> = [];
  const maxAttempts = Math.max(1, options.maxAttempts ?? 1);
  const retryDelayMs = Math.max(0, options.retryDelayMs ?? 0);
  let stopped = false;

  for (let index = 0; index < items.length; index += 1) {
    if (options.shouldStop?.()) {
      stopped = true;
      break;
    }

    const item = items[index];
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      if (options.shouldStop?.()) {
        stopped = true;
        break;
      }

      options.onAttempt?.(item, index, attempt);

      try {
        const result = await uploadOne(item, index, attempt);
        successes.push({ item, index, result, attempts: attempt });
        lastError = null;
        break;
      } catch (error) {
        lastError = toError(error);
        if (attempt < maxAttempts && !options.shouldStop?.()) {
          options.onRetry?.(item, index, attempt, lastError);
          await wait(retryDelayMs);
        }
      }
    }

    if (stopped) {
      break;
    }

    if (lastError) {
      failures.push({
        item,
        index,
        error: lastError,
        attempts: maxAttempts
      });
    }
  }

  return { successes, failures, stopped };
}
